import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import clsx from 'clsx';
import {getValidationLevel, ValidationObjectType} from './validators';
import {VALIDATION} from './VALIDATION';
import {WithdrawalSummaryValidation} from '../../RecommendsWithdrawal/WithdrawalSummaryValidation';
import {WithdrawalInfoCaptureProps} from '../../RecommendsWithdrawal/WithdrawalDestinationPlanForm';
import ChoiceDebug from './ChoiceDebug';
import {NEW_PLAN_ID} from './constants';

import theme, {themeColors} from '../../../../theme/Theme';

const classes = {
  rounded: {
    borderRadius: 4,
  },

  roundedTop: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },

  roundedBottom: {
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },

  shadows: {
    boxShadow: theme.shadows[2],
  },
  valid: {
    backgroundColor: '#c4e0b4',
  },

  invalid: {
    backgroundColor: '#ff9496',
  },

  partial: {
    backgroundColor: themeColors.lightWarning,
  },

  na: {
    backgroundColor: '#ccc',
  },
  validationIcon: {
    marginLeft: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto',
    width: 'auto !important', // preventing icon from looking funky when InfoCapture is inside InfoCapture
  },
  nothing: {}, // don't delete it. needed for getLinkedBG
};

interface RecommendPlanFormBaseProps {
  id?: string;
  summary: string | JSX.Element;
  disabled?: boolean;
  greyed?: boolean;
  validation?: ValidationObjectType;
  details?: JSX.Element | boolean;
  withdrawalValidation?: WithdrawalInfoCaptureProps;
  newPlanPotId?: string;
  fixed?: boolean;
  subItems?: JSX.Element | null;
  curoId?: string;
  indent?: boolean;
  isNewWrapperPlanForTransferOrContribution?: boolean;
  reccId: string | null;
  sameReview?: boolean;
  isSelected: boolean;
  isSubplan: boolean;
}

interface SingleChoiceProps extends RecommendPlanFormBaseProps {
  mode?: 'single';
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

interface MultipleChoiceProps extends RecommendPlanFormBaseProps {
  mode: 'multiple';
  handleChange: (id: string) => void;
}

export type RecommendPlanFormProps = SingleChoiceProps | MultipleChoiceProps;

const RecommendPlanForm: React.FC<RecommendPlanFormProps> = (props) => {
  const {
    id = '',
    summary,
    disabled = false,
    greyed = false,
    validation = undefined,
    details,
    withdrawalValidation,
    newPlanPotId = '',
    fixed = false,
    subItems = null,
    curoId,
    indent = false,
    isNewWrapperPlanForTransferOrContribution = false,
    reccId,
    sameReview,
    isSelected,
    isSubplan,
  } = props;

  const mode = props.mode ?? 'single';
  const expanded = Boolean(isSelected && details);
  const selected = isSelected;

  const validationLevel = getValidationLevel(validation);
  const getValidationBG = (lvl: keyof typeof classes) => {
    try {
      return classes[lvl];
    } catch {
      return '';
    }
  };
  const {
    linked = false,
    validatedBank = false,
    needsLink = false,
  } = withdrawalValidation || {};
  const getLinkedBG: () => keyof typeof classes = () => {
    if (!needsLink) {
      return 'nothing';
    }
    // eslint-disable-next-line no-nested-ternary
    if (linked) {
      if (validatedBank) {
        return 'valid';
      }
      return 'invalid';
    }
    if (validatedBank) {
      return 'partial';
    }

    return 'invalid';
  };
  const backgroundColor = getLinkedBG();

  return (
    <Box
      id={id}
      data-curo-id={curoId}
      data-pot-id={newPlanPotId || id}
      data-recc-id={reccId || 'New plan'}
      data-selected={isSelected ? 'selected' : 'not-selected'}
      data-is-subplan={isSubplan}
      data-is-new-plan={id === NEW_PLAN_ID ? 'yes' : 'no'}
      data-test="choice-option"
      key={id}
      className={indent ? 'indent' : ''}
      // eslint-disable-next-line no-nested-ternary
      mt={isSelected ? 2 : indent ? 0 : 1}
    >
      <ChoiceDebug
        newPlanPotId={newPlanPotId}
        id={id}
        sameReview={sameReview}
        reccId={reccId}
        curoId={curoId}
      />
      <Box
        display="flex"
        key={id}
        width="100%"
        paddingLeft={indent ? 4 : 0}
        mb={isSelected ? 3 : 1}
      >
        <Box ml={indent ? 2 : 0} width="100%">
          <Accordion
            className={clsx(
              validationLevel,
              'plan-wrapper',
              backgroundColor,
              sameReview ? 'same-review' : '',
              selected ? 'selected' : 'not-selected'
            )}
            sx={{
              borderRadius: 4,
              ...classes.shadows,
              ...classes[backgroundColor],
            }}
            data-type="InfoCapture"
            disabled={disabled}
            expanded={expanded}
          >
            <AccordionSummary
              expandIcon={null}
              style={{cursor: 'default', userSelect: 'text'}}
              sx={{
                ...(validationLevel
                  ? getValidationBG(validationLevel as keyof typeof classes)
                  : {}),
                ...(greyed ? classes.na : {}),
                ...(!expanded ? classes.roundedBottom : {}),
                ...classes.roundedTop,
                cursor: 'default',
                userSelect: 'text',
              }}
            >
              <Box
                display="flex"
                flexDirection="row"
                alignContent="center"
                justifyContent="flex-start"
                ml={1}
              >
                {mode === 'single' ? (
                  <FormControlLabel
                    value={id || NEW_PLAN_ID}
                    disabled={disabled || fixed}
                    label=""
                    checked={isSelected || false}
                    control={
                      <Radio
                        style={{backgroundColor: 'white'}}
                        checked={isSelected || false}
                        onChange={() =>
                          (props as SingleChoiceProps).handleChange(
                            null,
                            id || NEW_PLAN_ID
                          )
                        }
                        inputProps={{
                          'aria-label': 'set plan',
                        }}
                      />
                    }
                  />
                ) : (
                  <Checkbox
                    style={{
                      padding: '0 3px',
                      marginRight: 10,
                      backgroundColor: 'white',
                    }}
                    disabled={fixed || disabled}
                    inputProps={{
                      'aria-label': 'set plan',
                    }}
                    checked={isSelected || false}
                    onChange={() => {
                      (props as MultipleChoiceProps).handleChange(id!);
                    }}
                  />
                )}
                <Box
                  role="heading"
                  flex={1}
                  display="flex"
                  justifyContent="center"
                  flexDirection="column"
                >
                  {summary}
                </Box>
              </Box>
              <WithdrawalSummaryValidation
                validated={validatedBank}
                needsLink={needsLink}
                linked={linked}
              />
              {validation && (
                <Box sx={classes.validationIcon}>
                  {validationLevel === VALIDATION.VALID && <CheckIcon />}
                  {validationLevel === VALIDATION.PARTIALLY_VALID && <WarningIcon />}
                  {validationLevel === VALIDATION.INVALID && <NotInterestedIcon />}
                </Box>
              )}
            </AccordionSummary>

            {isSelected && details && (
              <AccordionDetails
                // 'plan-details' is used for e2e tests to find fields relevant to a plan.
                // For any new parent pension/bond for transfers and contributions
                // (but not if its a straight up "Set up new plan" recommendation)
                // we want to **not** have plan-details at the top level.
                // Why? Because inside new pension plans created for transfers/contributions, there
                // is another accordion within the NewPlan component which lets us know what fields
                // are in the parent. Otherwise if plan-details was included at the top level accordion
                // used in transfers/contributions then there would be two "plan-details".
                // One would wrap around just the parent plan and nothing else (this is good)
                // One would wrap around the parent plan and its subplans (this is bad as you couldnt differentiate
                // fields in subplans vs parent plans
                className={clsx(
                  isNewWrapperPlanForTransferOrContribution ? null : 'plan-details'
                )}
                sx={{
                  borderRadius: 4,
                  paddingLeft: 0.5,
                  paddingRight: 0.5,
                  paddingTop: 1,
                  margin: '8px 16px 16px',
                }}
              >
                {details}
              </AccordionDetails>
            )}
          </Accordion>
          {expanded && subItems && (
            <Box className="subplans" mt={2}>
              {subItems}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const RecommendPlanFormsStyled: React.FC<{children: React.ReactNode}> = ({
  children,
}) => (
  <Box
    data-test="Choice"
    sx={{
      width: '100%',
      marginBottom: theme.spacing(1),
    }}
  >
    {children}
  </Box>
);

export default RecommendPlanForm;
